/**
 * Tiny external store for the FeedbackModal open state. The store is
 * accessed through `useFeedbackModal()`; the FeedbackButton triggers it
 * with no context, and the global error interceptor / ErrorBoundary
 * "Report this" buttons trigger it pre-filled with an error context.
 *
 * External store + `useSyncExternalStore` so any component can read
 * without re-rendering the entire tree on toggle.
 */
import { useSyncExternalStore } from 'react';

export interface FeedbackPrefill {
  subject?: string;
  description?: string;
  severity?: 'low' | 'medium' | 'high';
  /** Sentry event ID to attach to the ticket. */
  sentryEventId?: string | null;
}

interface FeedbackState {
  open: boolean;
  prefill: FeedbackPrefill;
}

let state: FeedbackState = { open: false, prefill: {} };
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

export function openFeedbackModal(prefill: FeedbackPrefill = {}): void {
  state = { open: true, prefill };
  notify();
}

export function closeFeedbackModal(): void {
  state = { open: false, prefill: {} };
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): FeedbackState {
  return state;
}

export function useFeedbackModal(): FeedbackState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test-only — reset between tests so state doesn't leak across cases. */
export function __resetFeedbackStoreForTests(): void {
  state = { open: false, prefill: {} };
  notify();
}
